import { CHESTERTON, DOUGLASS_FOURTH, DOUGLASS_NARRATIVE, GETTYSBURG, HUXLEY, THOREAU, type PublicDomainText, type Source } from './texts';

export interface WritingExample {
  id: string;
  name: string;
  note: string;
  text: string;
  /** The library rubric this example is graded against. */
  rubricId: string;
  /** Present when the text is a public-domain work rather than authored for this tool. */
  source?: Source;
  /** Overrides the rubric's own assignment. */
  assignment?: string;
}

const published = (text: PublicDomainText, fields: Omit<WritingExample, 'text' | 'source'>): WritingExample =>
  ({ ...fields, text: text.text, source: text.source });

export const GETTYSBURG_ASSIGNMENT = `Read the address Abraham Lincoln delivered on November 19, 1863, at the dedication of the Soldiers' National Cemetery at Gettysburg, four and a half months after the battle. Write an essay that analyzes the rhetorical choices Lincoln makes to achieve his purpose.

${GETTYSBURG.text}`;

/**
 * Public-domain works and responses written for this tool. No learner submission is
 * committed or used here.
 */
export const EXAMPLES: readonly WritingExample[] = [
  // --- Argumentative ------------------------------------------------------------------------
  published(DOUGLASS_FOURTH, {
    id: 'douglass-fourth',
    rubricId: 'argumentative',
    name: 'Douglass — “What to the Slave Is the Fourth of July?” (1852)',
    note: 'A ceiling: a precise claim, layered evidence, and a counterclaim answered head-on.',
  }),
  published(THOREAU, {
    id: 'thoreau',
    rubricId: 'argumentative',
    name: 'Thoreau — “Civil Disobedience” (1849), opening',
    note: 'A strong claim built by reasoning and analogy, with little concrete evidence and only a brief nod to the opposing view.',
  }),
  {
    id: 'later-start',
    rubricId: 'argumentative',
    name: 'Student — “Schools should start later”',
    note: 'A clear opinion with one general reason; the objection is raised and dropped.',
    text: `I think schools should start later in the morning. Most students are really tired in first period and nobody is paying attention. I know I am always tired. If school started at 9:00 instead of 7:30 everyone would get more sleep and do better on there tests.

Teenagers need a lot of sleep. Scientists say teenagers need more sleep than adults. When you are tired you can't learn anything so it is a waste of time to have class that early. Also a lot of people miss the bus because it comes so early and then they are late.

Some people say that starting later would mess up sports and parents' jobs. But I still think it is a good idea.

In conclusion, schools should start later because students are tired. It would make everyone happier and do better in school.`,
  },

  // --- Explanatory --------------------------------------------------------------------------
  published(HUXLEY, {
    id: 'huxley',
    rubricId: 'explanatory',
    name: 'Huxley — “On a Piece of Chalk” (1868)',
    note: 'A ceiling: a question the reader wants answered, worked through with familiar comparisons and terms defined as they arrive.',
  }),
  {
    id: 'chalk-student',
    rubricId: 'explanatory',
    name: 'Student — “Where chalk comes from”',
    note: 'Some correct facts, no controlling idea, and terms used without explanation.',
    text: `Chalk is a white rock that is used for writing on chalkboards and for sidewalk chalk. It is found in a lot of places like England where there are the White Cliffs of Dover. Chalk is a type of limestone.

Chalk is made of calcium carbonate. It formed a long time ago in the ocean. There were tiny things in the ocean that died and fell to the bottom. Over millions of years it turned into rock. Then the ocean went away and now the chalk is on land.

Chalk is also used for other things. Farmers put it on fields and it is used in toothpaste. Sidewalk chalk today is usually made from a different material called gypsum.

That is where chalk comes from. It is very old and it is interesting that something so small can make a whole cliff.`,
  },

  // --- Narrative ----------------------------------------------------------------------------
  published(DOUGLASS_NARRATIVE, {
    id: 'douglass-narrative',
    rubricId: 'narrative',
    name: 'Douglass — Narrative, Chapter VII (1845)',
    note: 'A ceiling: a clear situation, scenes paced around the moments that matter, and reflection the events have earned.',
  }),
  {
    id: 'reading-student',
    rubricId: 'narrative',
    name: 'Student — “Learning to read with my grandma”',
    note: 'A clear sequence that mostly summarizes; the lesson is stated rather than earned.',
    text: `When I was in second grade I could not read very well. My teacher told my mom that I was behind. I was embarrassed because everyone else in my class could read chapter books and I could only read picture books.

My grandma lived with us that year. Every day after school she would sit with me at the kitchen table and we would read together. At first it was really hard and I didn't want to do it. Sometimes I cried. My grandma was very patient and she never got mad at me.

We read a lot of books. We read Frog and Toad and then we read some harder books. After a few months I was getting better. By the end of the year I could read chapter books like everyone else. My teacher said I improved a lot.

Now I love reading and I read every night before bed. This experience taught me that if you work hard you can do anything. I will always be thankful to my grandma for helping me.`,
  },

  // --- 6 Traits -----------------------------------------------------------------------------
  published(CHESTERTON, {
    id: 'chesterton',
    rubricId: 'six-traits',
    name: 'Chesterton — “A Piece of Chalk” (1909)',
    note: 'A ceiling for voice: a small errand turned into an idea, with an ending that recasts the opening.',
  }),
  {
    id: 'backpack-student',
    rubricId: 'six-traits',
    name: 'Student — “My backpack”',
    note: 'A sincere start with flat voice, ordinary words, choppy sentences and frequent errors.',
    text: `My backpack is blue. I have had it since 6th grade. It has alot of pockets. I keep my stuff in it.

In the big pocket I keep my binder and my books. In the small pocket I keep pencils and my phone charger. there is also a pocket on the side for my water bottle. My backpack is very heavy sometimes. It hurts my back.

My backpack is important to me because it has all my stuff in it. If I lost it I would be in big trouble. One time I left it on the bus and I was really worried but the bus driver found it. I was happy.

I think everyone has something like my backpack that is important to them. Thats why I like my backpack.`,
  },

  // --- Rhetorical analysis ------------------------------------------------------------------
  {
    id: 'gettysburg-exemplary',
    rubricId: 'rhetorical-analysis',
    assignment: GETTYSBURG_ASSIGNMENT,
    name: 'Gettysburg — exemplary analysis',
    note: 'A precise thesis; explains how structure and repetition move the audience, with quotations woven in.',
    text: `Lincoln was invited to Gettysburg to offer "a few appropriate remarks" at the dedication of a cemetery, and his audience expected a eulogy. In ten sentences he gives them one and then refuses it. By moving his listeners from the nation's past to the battlefield's present to the work still ahead, and by repeatedly insisting that words cannot do what the dead have done, Lincoln turns a ceremony of mourning into a demand that the living finish the war.

The address opens not with the battle but with the founding. "Four score and seven years ago" reaches back to 1776 in the elevated cadence of scripture, and it places the Declaration of Independence, not the Constitution, at the nation's birth. That choice matters. By defining the nation as "dedicated to the proposition that all men are created equal," Lincoln makes the war a test of that proposition, so that the soldiers buried at Gettysburg died for an idea rather than for a region.

The middle of the speech performs a reversal. Lincoln states the occasion's purpose — "We have come to dedicate a portion of that field" — and then takes it back: "we can not dedicate—we can not consecrate—we can not hallow—this ground." The three verbs climb from the civic to the sacred, and each is cancelled by the same negation. The dashes slow the sentence down until the audience feels the inadequacy he is describing. Having emptied the ceremony of its power, Lincoln gives that power to the soldiers, who "have consecrated it, far above our poor power to add or detract."

That reversal sets up the turn to the living. The word "dedicate" returns, but now the audience is its object: "It is for us the living, rather, to be dedicated here to the unfinished work." The final sentence piles up clauses joined by "that," each one a resolution, until it arrives at "government of the people, by the people, for the people." The triple repetition echoes the earlier three verbs, but where those were cancelled, these are affirmed. The sentence leaves the audience not grieving but committed.

Lincoln never names the Union, the Confederacy or slavery. By speaking only of "a new nation," "a great civil war" and "a new birth of freedom," he lets every listener supply the cause, and he makes the cemetery a promise rather than a grave.`,
  },
  {
    id: 'gettysburg-proficient',
    rubricId: 'rhetorical-analysis',
    assignment: GETTYSBURG_ASSIGNMENT,
    name: 'Gettysburg — proficient analysis',
    note: 'A defensible thesis and accurate evidence; analysis of each device is sound but does not show how they work together.',
    text: `In the Gettysburg Address, Abraham Lincoln uses an allusion to the nation's founding, repetition, and a contrast between words and actions to persuade his audience to keep fighting the Civil War so that the soldiers' deaths would mean something.

Lincoln begins with "Four score and seven years ago," which refers back to 1776 and the Declaration of Independence. This reminds the audience that the country was founded on the idea "that all men are created equal." By starting with the founding, Lincoln suggests that the war is about protecting the ideals the country started with.

Lincoln also uses repetition. He says "we can not dedicate—we can not consecrate—we can not hallow—this ground." Repeating "we can not" emphasizes that the people at the ceremony cannot make the ground more sacred than the soldiers already did. Later he repeats "the people" in "government of the people, by the people, for the people," which makes the ending memorable and stresses that democracy belongs to everyone.

Finally, Lincoln contrasts words with actions. He says "The world will little note, nor long remember what we say here, but it can never forget what they did here." This shows humility and honors the soldiers. It also shifts the responsibility to the audience, because if words are not enough, the living have to act.

Through these choices, Lincoln honors the dead while convincing the audience to continue the "unfinished work" of the war.`,
  },
  {
    id: 'gettysburg-summary',
    rubricId: 'rhetorical-analysis',
    assignment: GETTYSBURG_ASSIGNMENT,
    name: 'Gettysburg — summary, not analysis',
    note: 'Accurate on the occasion and the content, but retells what Lincoln says instead of how he says it.',
    text: `The Gettysburg Address was a speech given by Abraham Lincoln in 1863 during the Civil War. He gave it at Gettysburg, Pennsylvania, where a big battle had happened a few months before. The speech was to dedicate a cemetery for the soldiers who died there.

Lincoln starts by talking about how the country was founded 87 years earlier on the idea that all men are created equal. Then he says that the country is now in a civil war that is testing whether a country like this can survive. He says they are there to dedicate part of the battlefield as a cemetery.

Next Lincoln says that they cannot really make the ground holy because the soldiers who fought there already did that. He says people will not remember the speech but they will remember what the soldiers did.

At the end, Lincoln says that the living have to finish the work the soldiers started. He says the soldiers should not have died for nothing and that the nation should have a new birth of freedom. He ends by saying government of the people, by the people, for the people should not disappear.

The Gettysburg Address is a very famous speech. It is short but it is powerful, and it is one of the most important speeches in American history.`,
  },
  {
    id: 'gettysburg-misreading',
    rubricId: 'rhetorical-analysis',
    assignment: GETTYSBURG_ASSIGNMENT,
    name: 'Gettysburg — misreading',
    note: 'Gets the occasion and purpose wrong and names devices without explaining them.',
    text: `The Gettysburg Address was Abraham Lincoln's speech when he became president. He used a lot of rhetorical devices like ethos, pathos, and logos to convince people to vote for him.

Lincoln uses pathos when he talks about the soldiers. This makes people feel sad. He uses logos when he says four score and seven years, because that is a number. He also uses ethos because he is the president so people trust him.

Lincoln says the war is over and the North won, so now the country can be free. He says people will never forget the speech. This was true because everyone still knows it today.

Overall Lincoln used ethos, pathos, and logos very well and that is why the speech is so famous.`,
  },
];

export type ExampleId = string;
/**
 * The text the keyless tests score against the default rubric. Thoreau, because the keyword
 * fallback finds evidence for Claim and Focus in it; it finds none in Douglass.
 */
export const SAMPLE_TEXT: string = EXAMPLES.find((example) => example.id === 'thoreau')!.text;
